import { useState, type ReactElement } from "react";
import { useLocation } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState } from "../../components/api-state.js";
import { FormSummary } from "../../forms/fields.js";
import { planQueryFrom } from "../tenants/plan-query.js";

import { EstimateForm } from "./estimate-form.js";
import { EstimateSheet, type Estimate, type EstimatePreview } from "./estimate-sheet.js";
import { estimateApi, estimateUrls } from "./urls.js";

/**
 * 見積書（<c>/Estimate?PlanName=…</c>。料金プランのページの「この内容で見積書を発行する」）。<b>ログインは要らない</b>。
 *
 * 開くと選んだ内容の見積り（<c>GET /api/estimate</c>。保存しない）を出し、宛名を入れて「作成」を押すと
 * <c>POST /api/estimate</c> が保存して社内へメールで知らせ、保存した見積書を返す。その見積書を<b>印刷する画面</b>として出す
 * （見た目と印刷のしかたは請求書と同じ。紙面は <c>estimate-sheet.tsx</c>）。
 * 見積もる内容は URL のクエリのまま API へ渡す（<c>tenants/plan-query.ts</c> の形。確かめるのはサーバ）。
 * 作った見積書の下に「この内容で申し込む」を出し、同じ内容を新規申し込みへクエリで渡す（料金プランのページの見積り欄と同じ行き先。
 * ログインしていなければアカウント登録へ、戻り先に申し込みを付けて送る）。紙には出さない。
 *
 * <b>作った見積書は画面が持つだけ</b>——番号で引き直す公開の口は無い。開き直すと作成前に戻る。
 */
export function EstimatePage(): ReactElement | null {
    const { search } = useLocation();
    const query = new URLSearchParams(search).toString();
    const data = useApiData<EstimatePreview>(estimateApi.quote(query));
    const [created, setCreated] = useState<{ readonly estimate: Estimate; readonly signedIn: boolean } | null>(null);
    usePageMeta("見積書");
    // 作った見積書は紙面の頭から見せる（「作成」は作成前の画面の下の方にある）
    const show = (estimate: Estimate, signedIn: boolean): void => {
        setCreated({ estimate, signedIn });
        window.scrollTo(0, 0);
    };

    const back = (
        <div className="mb-4 flex items-center justify-between print:hidden">
            <a href={estimateUrls.price(query)} className="btn-secondary btn-sm">料金プランへ戻る</a>
            {created === null
                ? null
                : (
                    <button type="button" className="btn-secondary btn-sm" onClick={() => { globalThis.print(); }}>
                        印刷する
                    </button>
                )}
        </div>
    );

    if (created !== null) {
        const signup = planQueryFrom(new URLSearchParams(search));
        return (
            <div className="page max-w-3xl">
                {back}
                <EstimateSheet estimate={created.estimate} />
                {/* 申し込みはこの画面でいちばん強い導線（btn-accent。料金プランのページの見積り欄と同じ見た目） */}
                <div className="mt-6 flex print:hidden">
                    <a
                        href={created.signedIn ? estimateUrls.signup(signup) : estimateUrls.register(signup)}
                        className="btn-accent grow" data-estimate-apply=""
                    >
                        この内容で申し込む
                    </a>
                </div>
            </div>
        );
    }

    // 申し込めない内容（人数の範囲・選べない組み合わせなど）は、API の断りをそのまま出す
    if (data.state === "failed" && data.status === 400 && data.errors !== undefined) {
        return (
            <div className="page max-w-3xl">
                {back}
                <h1 className="page-title">見積書を作れませんでした</h1>
                <FormSummary messages={Object.values(data.errors).flat()} />
            </div>
        );
    }
    if (data.state !== "ready") {
        return <ApiState data={data} what="見積書" />;
    }

    return (
        <div className="page max-w-3xl">
            {back}
            <EstimateForm query={query} preview={data.value} onCreated={show} />
        </div>
    );
}
